import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { joinOrganization, previewInvite } from '../../api/organizationApi';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import './SchoolPlan.css';

const SUCCESS = new Set(['joined', 'joined_existing', 'already_member', 'pro_pending']);
// 다시 눌러도 결과가 바뀔 수 있는 경우만 버튼을 다시 보여준다. 나머지는 같은 결과가 나오므로 막는다.
const RETRYABLE = new Set(['in_progress', 'rate_limited', 'error']);

const PERKS = ['AI 생기부·채점·평가계획서를 PRO로', '학교 관리자는 사용 건수만 봐요', '내가 만든 내용은 나만 봐요'];

const storageKey = (uid, token) => `wq_org_join:${uid}:${token}`;

const readStored = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const writeStored = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // 저장소가 막혀도 attemptRef 가 같은 탭 안의 중복 호출을 막는다.
  }
};

const titleFor = (result, orgName) => {
  if (!result) return '';
  if (result.outcome === 'already_member') return `이미 ${orgName || '학교'} 소속이에요`;
  if (SUCCESS.has(result.outcome)) return `${orgName || '학교'}에 합류했어요`;
  if (result.outcome === 'seats_full') return '자리가 모두 찼어요';
  if (result.outcome === 'invalid_link') return '열 수 없는 초대 링크예요';
  if (result.outcome === 'other_school') return '이미 다른 학교 소속이에요';
  return '합류하지 못했어요';
};

const bodyFor = (message, title) => {
  const text = (message || '').trim();
  if (!title || !text.startsWith(title)) return text;
  return text.slice(title.length).replace(/^[.\s]+/, '');
};

const JoinOrganizationPage = () => {
  const { token = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const [invite, setInvite] = useState(null);
  const [result, setResult] = useState(null);
  const [joining, setJoining] = useState(false);
  const [networkError, setNetworkError] = useState('');
  const attemptRef = useRef(false);
  const autoJoin = new URLSearchParams(location.search).get('go') === '1';

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;
    previewInvite(token, currentUser)
      .then((data) => { if (!cancelled) setInvite(data); })
      .catch(() => { if (!cancelled) setInvite({ valid: false, message: '초대 링크를 확인하지 못했어요. 잠시 후 새로고침해 주세요.' }); });
    return () => { cancelled = true; };
  }, [authLoading, currentUser, token]);

  useEffect(() => {
    if (!currentUser) return;
    const stored = readStored(storageKey(currentUser.uid, token));
    if (stored) {
      setResult(stored);
      if (!RETRYABLE.has(stored.outcome)) attemptRef.current = true;
    }
  }, [currentUser, token]);

  const join = useCallback(async () => {
    if (!currentUser || attemptRef.current) return;
    attemptRef.current = true;
    setJoining(true);
    setNetworkError('');
    try {
      const data = await joinOrganization(currentUser, token);
      setResult(data);
      writeStored(storageKey(currentUser.uid, token), data);
      if (SUCCESS.has(data.outcome)) {
        fetchSubscriptionStatus?.(true);
      }
      if (RETRYABLE.has(data.outcome)) attemptRef.current = false;
    } catch (_error) {
      attemptRef.current = false;
      setNetworkError('연결이 잠시 끊겼어요. 아래 버튼을 한 번 더 눌러 주세요.');
    } finally {
      setJoining(false);
    }
  }, [currentUser, fetchSubscriptionStatus, token]);

  useEffect(() => {
    if (!autoJoin || !currentUser || !invite?.valid || invite.already_member || result) return;
    join();
    navigate(`/join/${token}`, { replace: true });
  }, [autoJoin, currentUser, invite, join, navigate, result, token]);

  const loginPath = `/auth?redirect=${encodeURIComponent(`/join/${token}?go=1`)}`;
  const orgName = result?.org_name || invite?.org_name || '';
  const shownResult = result || (invite?.already_member ? { outcome: 'already_member', message: '' } : null);

  return (
    <main className="school-join">
      <Helmet>
        <title>{orgName ? `${orgName} 초대` : '학교 초대'} | 왓퀴즈</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {!invite && <div className="school-card school-skeleton" aria-label="불러오는 중" />}

      {invite && !invite.valid && (
        <section className="school-card" data-testid="join-invalid">
          <span className="school-join-badge is-muted" aria-hidden="true">!</span>
          <h1>열 수 없는 초대 링크예요</h1>
          <p>{invite.message}</p>
          <div className="school-join-actions">
            <Link className="school-btn-secondary" to="/home">왓퀴즈 둘러보기</Link>
          </div>
        </section>
      )}

      {invite?.valid && shownResult && (
        <section className="school-card" data-testid={`join-result-${shownResult.outcome}`}>
          <span
            className={`school-join-badge ${SUCCESS.has(shownResult.outcome) ? 'is-success' : 'is-muted'}`}
            aria-hidden="true"
          >
            {SUCCESS.has(shownResult.outcome) ? '✓' : '!'}
          </span>
          <h1>{titleFor(shownResult, orgName)}</h1>
          {bodyFor(shownResult.message, titleFor(shownResult, orgName)) && (
            <p>{bodyFor(shownResult.message, titleFor(shownResult, orgName))}</p>
          )}
          <div className="school-join-actions">
            {SUCCESS.has(shownResult.outcome) && (
              <Link className="school-btn-primary" to="/home">왓퀴즈 시작하기</Link>
            )}
            {shownResult.outcome === 'seats_full' && (
              <>
                <Link className="school-btn-primary" to="/pricing">개인 PRO 알아보기</Link>
                <Link className="school-btn-secondary" to="/home">무료로 둘러보기</Link>
              </>
            )}
            {RETRYABLE.has(shownResult.outcome) && (
              <button type="button" className="school-btn-primary" onClick={join} disabled={joining}>
                {joining ? '합류하는 중…' : '다시 합류하기'}
              </button>
            )}
            {!SUCCESS.has(shownResult.outcome) && !RETRYABLE.has(shownResult.outcome) && shownResult.outcome !== 'seats_full' && (
              <Link className="school-btn-secondary" to="/home">홈으로</Link>
            )}
          </div>
        </section>
      )}

      {invite?.valid && !shownResult && (
        <section className="school-card" data-testid="join-invite">
          <span className="school-join-badge" aria-hidden="true">{(orgName || '학').slice(0, 1)}</span>
          <p className="school-eyebrow">{invite.school_level || '학교'} 초대</p>
          <h1>{orgName}에서<br />선생님을 초대했어요</h1>
          <ul className="school-join-perks">
            {PERKS.map((perk) => <li key={perk}>{perk}</li>)}
          </ul>
          {!invite.has_open_seats && (
            <p className="school-error" role="status">지금은 남은 자리가 없어요. 학교 관리자 선생님께 자리 추가를 요청해 주세요.</p>
          )}
          {networkError && <p className="school-error" role="alert">{networkError}</p>}
          <div className="school-join-actions">
            {currentUser ? (
              <button
                type="button"
                className="school-btn-primary"
                onClick={join}
                disabled={joining || !invite.active || !invite.has_open_seats}
              >
                {joining ? '합류하는 중…' : '합류하고 PRO 받기'}
              </button>
            ) : (
              <>
                <Link className="school-btn-primary" to={loginPath}>로그인하고 합류하기</Link>
                <p className="school-form-foot">처음이면 로그인 화면에서 바로 가입할 수 있어요.</p>
              </>
            )}
          </div>
        </section>
      )}
    </main>
  );
};

export default JoinOrganizationPage;
